import {useRef} from "react";
import {UseFormReturn} from "react-hook-form";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";

import {Sheet, SheetContent} from "@/components/ui/sheet";
import {SheetHelpHeader} from "@/src/modules/help/components/sheet-help-header";
import {useCancelHelpTourOnSheetClose} from "@/src/modules/help/hooks/use-cancel-help-tour-on-sheet-close";

import {useInvalidate, useToast} from "../../core/hooks";
import {useStoredUserProperty} from "../../core/store/user-property";
import useCreateVehicle from "../hooks/use-create-vehicle";
import useUpdateVehicle from "../hooks/use-update-vehicle";
import {applyVehicleWriteFieldErrors} from "../lib/apply-vehicle-write-field-errors";
import {VEHICLE_HELP_TOURS} from "../lib/vehicle-help";
import {buildVehicleUpdatePayload, buildVehicleWritePayload} from "../services/vehicle-write-payload";
import {Vehicle, VehicleFormData} from "../types";
import {VehicleForm} from "./vehicle-form";

type VehiclesFormSheetProps = {
  open: boolean;
  vehicle?: Vehicle | null;
  prefill?: {plate?: string; state?: string};
  onClose: () => void;
};

export function VehiclesFormSheet({open, vehicle, prefill, onClose}: VehiclesFormSheetProps) {
  const {t} = useTranslation("vehicles");
  const userProperty = useStoredUserProperty()!;
  const toast = useToast();
  const invalidate = useInvalidate("vehicles");
  const navigate = useNavigate();
  const formRef = useRef<UseFormReturn<VehicleFormData>>(null);
  const {mutateAsync: createVehicle} = useCreateVehicle();
  const {mutateAsync: updateVehicle} = useUpdateVehicle();
  const isEdit = vehicle != null;

  useCancelHelpTourOnSheetClose(open);

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      return;
    }
    onClose();
  };

  const handleInit = (form: UseFormReturn<VehicleFormData>) => {
    formRef.current = form;
  };

  const handleSubmit = async (data: VehicleFormData) => {
    try {
      const writeBase = {
        color: data.color,
        state: data.state,
        year: data.year,
        property_id: userProperty.id,
        city: "",
        house_hold_id: data.house_hold_id ?? undefined,
      };
      if (vehicle) {
        const dirtyFields = formRef.current?.formState.dirtyFields ?? {};
        const payload = buildVehicleUpdatePayload(data, writeBase, dirtyFields);
        await updateVehicle({id: vehicle.vehicle_id, data: payload});
        invalidate();
      } else {
        const payload = buildVehicleWritePayload(data, writeBase);
        const created = await createVehicle(payload);
        navigate(`/parking-enforcement/${created.vehicle_id}`);
      }
      toast.success(vehicle ? t("toast.updated") : t("toast.created"));
      onClose();
    } catch (error) {
      if (formRef.current) {
        applyVehicleWriteFieldErrors(error, formRef.current);
      }
    }
  };

  return (
    <Sheet
      open={open}
      askConfirmation={() => {
        if (!formRef.current) return false;
        return Object.keys(formRef.current.formState.dirtyFields).length > 0;
      }}
      onOpenChange={handleOpenChange}
    >
      <SheetContent className="flex min-h-0 flex-col gap-0 overflow-hidden p-0">
        <SheetHelpHeader
          title={isEdit ? "Edit vehicle" : "Add vehicle"}
          createTourId={VEHICLE_HELP_TOURS.formCreate}
          showCreateTour={!isEdit}
        />
        <VehicleForm
          layout="sheet"
          vehicle={vehicle}
          prefill={prefill}
          householdConfig={
            vehicle
              ? {mode: "edit", propertyId: vehicle.property_id ?? userProperty.id}
              : {mode: "enforcement-create", propertyId: userProperty.id}
          }
          onInit={handleInit}
          onSubmit={handleSubmit}
        />
      </SheetContent>
    </Sheet>
  );
}
